import { useEffect, useMemo, useState } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { api } from '../lib/api';
import type { HeatmapItem } from '../lib/types';
import { readBbox } from '../lib/mapBbox';

const MIN_ZOOM_FOR_GEOM = 11; // por debajo, demasiado polígonos → cap server
const BBOX_DEBOUNCE_MS = 600;

interface AGEBProperties {
  cveAgeb: string;
  score: number;
  inv: number | null;
  listed: number;
}

export function HeatmapLayer() {
  const [items, setItems] = useState<HeatmapItem[]>([]);
  // Contador de fetch: identidad estable para el key del <GeoJSON> (ver
  // abajo) y guard contra respuestas fuera de orden.
  const [fetchSeq, setFetchSeq] = useState(0);
  const [zoom, setZoom] = useState(0);
  const map = useMap();

  useEffect(() => {
    setZoom(map.getZoom());
    let timer: number | undefined;
    // Un controller POR REQUEST: antes se creaba dentro del callback y
    // nadie lo abortaba jamás — requests en vuelo sobrevivían al unmount
    // y podían aterrizar fuera de orden pintando polígonos de otro bbox.
    let inflight: AbortController | null = null;
    const refetch = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const z = map.getZoom();
        setZoom(z);
        if (z < MIN_ZOOM_FOR_GEOM) {
          inflight?.abort();
          inflight = null;
          setItems([]); // overview muy zoom-out: ocultamos para perf
          return;
        }
        // Mismo guard que los markers: sin alto de contenedor, el bbox
        // sale degenerado y el heatmap se queda vacío en la primera carga.
        const bbox = readBbox(map);
        if (!bbox) { map.invalidateSize(); return; }
        inflight?.abort();
        const ctrl = new AbortController();
        inflight = ctrl;
        api
          .heatmap({ withGeometry: true, bbox, limit: 500 }, ctrl.signal)
          .then((r) => {
            if (inflight !== ctrl) return; // llegó tarde — ya hay una más nueva
            setItems(r.items);
            setFetchSeq((s) => s + 1);
          })
          .catch(() => {});
      }, BBOX_DEBOUNCE_MS);
    };
    refetch();
    map.on('moveend', refetch);
    map.on('zoomend', refetch);
    return () => {
      map.off('moveend', refetch);
      map.off('zoomend', refetch);
      inflight?.abort();
      window.clearTimeout(timer);
    };
  }, [map]);

  const fc = useMemo<FeatureCollection<Geometry, AGEBProperties>>(() => ({
    type: 'FeatureCollection',
    features: items
      .filter((i) => !!i.geometry)
      .map((i): Feature<Geometry, AGEBProperties> => ({
        type: 'Feature',
        geometry: i.geometry as Geometry,
        properties: {
          cveAgeb: i.cve_ageb,
          score: Number(i.composite_vacancy_score ?? 0),
          inv: i.inv_pct_deshabitadas,
          listed: i.properties_listed,
        },
      })),
  }), [items]);

  if (zoom < MIN_ZOOM_FOR_GEOM) return null;

  return (
    <GeoJSON
      // react-leaflet no reacciona a cambios de `data` — el remount por key
      // es obligado. `key={items.length}` descartaba updates de IGUAL
      // longitud (pan de una zona con 220 AGEBs a otra con 220 → polígonos
      // congelados). El contador de fetch cambia en CADA respuesta aplicada.
      key={fetchSeq}
      data={fc}
      style={(f) => {
        const score = (f?.properties as AGEBProperties | undefined)?.score ?? 0;
        return {
          fillColor: scoreToColor(score),
          fillOpacity: 0.45,
          color: '#0a0f1f',
          weight: 0.5,
        };
      }}
      onEachFeature={(feature, layer) => {
        const p = feature.properties as AGEBProperties;
        // Valores del API escapados antes de concatenar HTML — cveAgeb es
        // un string externo y esto era el único sitio con interpolación cruda.
        const esc = (s: string) =>
          s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        layer.bindPopup(
          `<div class="popup-card">
            <div style="font-weight:600">AGEB ${esc(p.cveAgeb.trim())}</div>
            <div style="margin-top:4px">Score: <b>${p.score.toFixed(1)}</b></div>
            ${p.inv !== null ? `<div>INV % deshab.: ${Number(p.inv)}</div>` : ''}
            <div>Properties listadas: ${Number(p.listed)}</div>
          </div>`,
        );
      }}
    />
  );
}

// Escala perceptual (viridis-like) en hex. 0=verde, 100=rojo intenso.
export function scoreToColor(score: number): string {
  const s = Math.max(0, Math.min(100, score));
  const stops = [
    { p: 0,   c: [34, 197, 94] },    // green-500
    { p: 25,  c: [132, 204, 22] },   // lime-500
    { p: 50,  c: [251, 191, 36] },   // amber-400
    { p: 75,  c: [249, 115, 22] },   // orange-500
    { p: 100, c: [239, 68, 68] },    // red-500
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (a && b && s >= a.p && s <= b.p) {
      const t = (s - a.p) / (b.p - a.p);
      const r = Math.round(a.c[0]! + (b.c[0]! - a.c[0]!) * t);
      const g = Math.round(a.c[1]! + (b.c[1]! - a.c[1]!) * t);
      const bl = Math.round(a.c[2]! + (b.c[2]! - a.c[2]!) * t);
      return `rgb(${r},${g},${bl})`;
    }
  }
  return 'rgb(128,128,128)';
}
